import * as React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useAppTranslation} from "../services/i18n";
import {Avatar, Box, Card, CardContent, Grid, Paper, Tooltip, Typography} from "@mui/material";
import {BarChartRounded, CheckBoxOutlined, FileDownloadOutlined, PeopleAltOutlined, TaskOutlined} from "@mui/icons-material";
import {SelectFormField} from "components/form/SelectFormField";
import {OptionValue} from "model/form";
import {Formik, FormikErrors, FormikProps} from "formik";
import Button from "@mui/material/Button";
import {useAppDispatch} from "store";
import {
    fetchReportingStudyTimesContents,
    fetchReportingStudyTimesContentsXls,
    fetchReportingStudyTimesStudents,
    fetchReportingStudyTimesStudentsXls,
    fetchReportingTestAnswers,
    fetchReportingTestAnswersXls,
    fetchReportingTestAttempts,
    fetchReportingTestAttemptsXls
} from "store/reporting";
import {ContentSelectFormField} from "components/form/ContentSelectFormField";
import {UserSelectFormField} from "components/form/UserSelectFormField";
import {
    CompanySpacesReportingStudyTimesStudentsGetRequest,
    type ReportingStudyTimesGroupedContentsContentsResponse,
    ReportingStudyTimesGroupedStudentsStudentsResponse,
    ReportingTestAnswerIndexResponse,
    ReportingTestAttemptIndexResponse
} from "generated-api";
import {ReportTestAnswersTable} from "components/reports/ReportTestAnswersTable";
import IconButton from "@mui/material/IconButton";
import {downloadFileAsBlob} from "../helpers/api";
import {ReportTestAttemptsTable} from "components/reports/ReportTestAttemptsTable";
import {ReportStudyTimes} from "components/reports/ReportStudyTimes";

const ReportCard = ({icon, color, title, description, onClick}: { icon: JSX.Element, color: string, title: string, description: string, onClick: () => void }) => {
    // the legacy <Grid item xs={12} md={4}> cell; a Grid v2 size would need a Grid container
    // parent, and that lays the cards out 3+1 instead of the original 2x2 (33% + gap wraps)
    return <Box sx={{flexGrow: 0, flexBasis: {xs: '100%', md: '33.333%'}, maxWidth: {xs: '100%', md: '33.333%'}}}>
        <Card sx={{
            height: '100%',
            maxWidth: '320px',
            border: '1px solid #E5E5E5',
            borderRadius: '16px',
            boxShadow: 'none',
            padding: '16px',
            '&:hover': {
                opacity: .8,
                cursor: 'pointer',
            }
        }} onClick={onClick}>
            <CardContent>
                <Avatar sx={{
                    background: 'color-mix(in srgb, ' + color + ', transparent 80%)',
                    color,
                    width: 48,
                    height: 48,
                    mb: 2,
                    '& svg': {
                        fontSize: 28
                    },
                    borderRadius: '12px',
                }}>
                    {icon}
                </Avatar>
                <Typography variant="h6" component="h3" gutterBottom sx={{fontWeight: 600, fontSize: '18px'}}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{fontSize: '14px', lineHeight: 1.6}}>
                    {description}
                </Typography>
            </CardContent>
        </Card>
    </Box>;
};

const HowItWorksStep = ({number, title, description}: { number: number, title: string, description: string }) => {
    return (
        <Box sx={{display: 'flex', gap: 2, mb: 3, fontSize: '16px'}}>
            <Avatar sx={{
                bgcolor: 'var(--color-success)',
                width: 40,
                height: 40,
                flexShrink: 0,
                fontSize: '18px',
                fontWeight: 600
            }}>
                {number}
            </Avatar>
            <Box>
                <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 0.5}}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {description}
                </Typography>
            </Box>
        </Box>
    );
};

export interface ReportDataType {
    studyTimesContents?: ReportingStudyTimesGroupedContentsContentsResponse,
    studyTimesStudents?: ReportingStudyTimesGroupedStudentsStudentsResponse,
    testAnswers?: ReportingTestAnswerIndexResponse[],
    testAttempts?: ReportingTestAttemptIndexResponse[]
}

type ReportType = keyof ReportDataType;

interface FilterType {
    reportType?: ReportType;
    contentId?: number[];
    userInCompanySpaceId?: number[];
    state?: 'all' | 'active' | 'archived';
    period?: 'year' | 'month';
    yearFrom?: number,
    yearTo?: number,
    page: number;
    perPage: number;
    action: 'fetch' | 'export';
}

const ReportsMenu = ({setReportType}: { setReportType: (reportType: ReportType) => void }) => {
    const t = useAppTranslation();

    return <Box sx={{p: 4, backgroundColor: '#F5F5F5'}}>
        <Box sx={{mb: 5, fontSize: '24px', textAlign: 'center'}}>
            {t('reports.description')}
        </Box>

        <Box sx={{display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '20px'}}>
            <ReportCard
                color={'var(--color-success)'}
                icon={<BarChartRounded/>}
                title={t('reports.cards.studyTimesContents.title')}
                description={t('reports.cards.studyTimesContents.description')}
                onClick={() => setReportType('studyTimesContents')}
            />
            <ReportCard
                color={'var(--color-info)'}
                icon={<PeopleAltOutlined/>}
                title={t('reports.cards.studyTimesStudents.title')}
                description={t('reports.cards.studyTimesStudents.description')}
                onClick={() => setReportType('studyTimesStudents')}
            />
            <ReportCard
                color={'var(--color-warning)'}
                icon={<CheckBoxOutlined/>}
                title={t('reports.cards.testAnswers.title')}
                description={t('reports.cards.testAnswers.description')}
                onClick={() => setReportType('testAnswers')}
            />
            <ReportCard
                color={'var(--color-purple)'}
                icon={<TaskOutlined/>}
                title={t('reports.cards.testAttempts.title')}
                description={t('reports.cards.testAttempts.description')}
                onClick={() => setReportType('testAttempts')}
            />
        </Box>

        <Box sx={{mt: 4}}>
            <Box sx={{maxWidth: 600, margin: '0 auto'}}>
                <Typography variant="h5" component="h2" sx={{fontWeight: 600, mb: 4, textAlign: 'left', fontSize: '20px'}}>
                    {t('reports.howItWorks.title')}
                </Typography>
                <HowItWorksStep
                    number={1}
                    title={t('reports.howItWorks.steps.selectType.title')}
                    description={t('reports.howItWorks.steps.selectType.description')}
                />
                <HowItWorksStep
                    number={2}
                    title={t('reports.howItWorks.steps.setTimeRange.title')}
                    description={t('reports.howItWorks.steps.setTimeRange.description')}
                />
                <HowItWorksStep
                    number={3}
                    title={t('reports.howItWorks.steps.refineFilters.title')}
                    description={t('reports.howItWorks.steps.refineFilters.description')}
                />
                <HowItWorksStep
                    number={4}
                    title={t('reports.howItWorks.steps.generateExport.title')}
                    description={t('reports.howItWorks.steps.generateExport.description')}
                />
            </Box>
        </Box>
    </Box>;

}

const MIN_YEAR = 2023;
const MAX_YEAR = new Date().getFullYear();

export const ReportsPage = () => {
    const t = useAppTranslation();
    const dispatch = useAppDispatch();
    const [reportType, setReportType] = useState<ReportType>();
    const [data, setData] = useState<ReportDataType>();
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadMore, setIsLoadMore] = useState(false);
    const [filter, setFilter] = useState<FilterType>();

    const initialValues = useMemo(() => {
        const f: FilterType = {
            reportType,
            period: 'year',
            yearFrom: MIN_YEAR,
            yearTo: MAX_YEAR,
            page: 1,
            perPage: 20,
            state: 'all',
            action: 'fetch'
        }
        setFilter(undefined);
        setData(undefined);
        setIsLoadMore(false);
        return f;

    }, [reportType]);

    const handleFilter = useCallback(async (values: FilterType) => {
        setFilter(values);
    }, []);

    const createRequest = useCallback((filter: FilterType, forExport?: boolean): CompanySpacesReportingStudyTimesStudentsGetRequest => {
        const hasPeriods = filter.reportType === 'studyTimesStudents' || filter.reportType === 'studyTimesContents';
        const startDate = filter.yearFrom ? new Date(filter.yearFrom + '-01-01') : new Date('2023-01-01');
        const endDate = filter.yearTo ? new Date(filter.yearTo + '-12-31') : new Date();
        return {
            contentId: filter.contentId,
            userInCompanySpaceId: filter.userInCompanySpaceId,
            state: filter.state === 'active' ? 'active' : (filter.state === 'archived' ? 'revoked' : undefined),
            startDate,
            endDate,
            period: hasPeriods ? filter.period : undefined,
            perPage: forExport ? undefined : filter.perPage,
            page: forExport ? undefined : filter.page,
        }
    }, []);

    const fetchData = useCallback(async (filter: FilterType) => {
        setIsLoading(true);
        try {
            const request = createRequest(filter);

            if (filter.reportType === 'studyTimesContents') {
                const res = await dispatch(fetchReportingStudyTimesContents(request));
                if (!('error' in res)) {
                    const data = res.payload;
                    setData(current => {
                        const studyTimesContents: ReportingStudyTimesGroupedContentsContentsResponse = filter.page === 1 || !current?.studyTimesContents?.records
                            ? data
                            : {
                                periods: data.periods,
                                records: [...current.studyTimesContents.records, ...data.records]

                            };
                        setIsLoadMore(studyTimesContents?.records?.length === filter.page * filter.perPage)
                        return {...(current || {}), studyTimesContents};
                    });
                }

            } else if (filter.reportType === 'studyTimesStudents') {
                const res = await dispatch(fetchReportingStudyTimesStudents(request));
                if (!('error' in res)) {
                    const data = res.payload;
                    setData(current => {
                        const studyTimesStudents = filter.page === 1 || !current?.studyTimesStudents?.records
                            ? data
                            : {
                                periods: data.periods,
                                records: [...current.studyTimesStudents.records, ...data.records]

                            };
                        setIsLoadMore(studyTimesStudents?.records?.length === filter.page * filter.perPage)
                        return {...(current || {}), studyTimesStudents};
                    });
                }

            } else if (filter.reportType === 'testAnswers') {
                const res = await dispatch(fetchReportingTestAnswers(request));
                if (!('error' in res)) {
                    const data = res.payload;
                    setData(current => {
                        const testAnswers = filter.page === 1 || !current?.testAnswers ? data : [...current.testAnswers, ...data];
                        setIsLoadMore(testAnswers.length === filter.page * filter.perPage)
                        return {...(current || {}), testAnswers};
                    });
                }

            } else if (filter.reportType === 'testAttempts') {
                const res = await dispatch(fetchReportingTestAttempts(request));
                if (!('error' in res)) {
                    const data = res.payload;
                    setData(current => {
                        const testAttempts = filter.page === 1 || !current?.testAttempts ? data : [...current.testAttempts, ...data];
                        setIsLoadMore(testAttempts.length === filter.page * filter.perPage)
                        return {...(current || {}), testAttempts};
                    });
                }

            }
        } finally {
            setIsLoading(false);
        }
    }, [createRequest, dispatch]);

    const fetchExport = useCallback(async (filter: FilterType) => {
        setIsLoading(true);
        try {
            const request = createRequest(filter, true);

            let response;
            if (filter.reportType === 'studyTimesContents') {
                response = await dispatch(fetchReportingStudyTimesContentsXls(request));
            } else if (filter.reportType === 'studyTimesStudents') {
                response = await dispatch(fetchReportingStudyTimesStudentsXls(request));
            } else if (filter.reportType === 'testAnswers') {
                response = await dispatch(fetchReportingTestAnswersXls(request));
            } else if (filter.reportType === 'testAttempts') {
                response = await dispatch(fetchReportingTestAttemptsXls(request));
            }

            if (response && !('error' in response)) {
                await downloadFileAsBlob(response, `Report-${filter.reportType}-${new Date().toISOString()}.xlsx`)
            }
        } finally {
            setIsLoading(false);
        }
    }, [createRequest, dispatch]);

    const handleValidate = useCallback((values: FilterType) => {
        let errors = {} as FormikErrors<FilterType>;
        if (values.yearFrom && values.yearTo) {
            if (values.yearFrom > values.yearTo) {
                errors.yearTo = t('reports.filter.invalidInterval');
                errors.yearFrom = t('reports.filter.invalidInterval');
            }
        }
        return errors;
    }, [t]);

    const handleFetch = useCallback((e: any, props: FormikProps<FilterType>) => {
        props.setFieldValue('action', 'fetch');
        return props.handleSubmit(e);
    }, []);

    const handleExport = useCallback((e: any, props: FormikProps<FilterType>) => {
        props.setFieldValue('action', 'export');
        return props.handleSubmit(e);
    }, []);

    const handleFilterReset = useCallback((e: any, props: FormikProps<FilterType>) => {
        props.setValues(initialValues);
        return props.handleSubmit(e);
    }, [initialValues]);

    const reportTypeOptions = useMemo((): OptionValue[] => [
        {value: 'studyTimesContents', label: t('reports.reportTypes.studyTimesContents')},
        {value: 'studyTimesStudents', label: t('reports.reportTypes.studyTimesStudents')},
        {value: 'testAnswers', label: t('reports.reportTypes.testAnswers')},
        {value: 'testAttempts', label: t('reports.reportTypes.testAttempts')},
    ], [t]);

    const periodOptions = useMemo((): OptionValue[] => [
        {value: 'year', label: t('reports.period.yearly')},
        {value: 'month', label: t('reports.period.monthly')},
    ], [t]);

    const yearOptions = useMemo((): OptionValue[] => {
        return Array.from(
            {length: MAX_YEAR - MIN_YEAR + 1},
            (_, i) => MIN_YEAR + i
        ).map(y => ({value: y, label: String(y)}));
    }, []);

    const stateOptions = useMemo((): OptionValue[] => [
        {value: 'all', label: t('reports.state.all')},
        {value: 'active', label: t('reports.state.active')},
        {value: 'archived', label: t('reports.state.archived')},
    ], [t]);

    const isFilterUsed = useMemo(() => {
        return filter && JSON.stringify({...filter, action: 'fetch'}) !== JSON.stringify(initialValues);
    }, [filter, initialValues]);

    const hasResults = useMemo(() => {
        if (!data) {
            return true;
        }
        if (data.testAnswers) {
            return data.testAnswers.length > 0;
        }
        if (data.testAttempts) {
            return data.testAttempts.length > 0;
        }
        if (data.studyTimesContents) {
            return (data.studyTimesContents.records?.length || 0) > 0;
        }
        if (data.studyTimesStudents) {
            return (data.studyTimesStudents.records?.length || 0) > 0;
        }
        return true;
    }, [data]);

    useEffect(() => {
        if (filter?.action === 'fetch') {
            fetchData(filter).then();
        } else if (filter?.action === 'export') {
            fetchExport(filter).then();
        }
    }, [filter, fetchData, fetchExport]);

    const hasPeriods = reportType === 'studyTimesStudents' || reportType === 'studyTimesContents';
    const hasContent = reportType !== 'studyTimesContents';
    const hasStudent = reportType !== 'studyTimesStudents';

    return <>
        <Paper sx={{p: 2, pl: 3, pr: 3, boxShadow: 'none', borderBottom: '1px solid silver', borderRadius: 0}}>
            <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                onSubmit={handleFilter}
                validate={handleValidate}
            >
                {(props) => <form onSubmit={(e) => handleFetch(e, props)}>
                    <Grid container spacing={2} sx={{alignItems: 'center'}}>
                        <Grid size={{xs: 12, sm: 12, md: 6, lg: 2, xl: 2}}>
                            <SelectFormField name={'reportType'}
                                label={t('reports.filter.reportType')}
                                placeholder={t('reports.filter.selectReportType')}
                                options={reportTypeOptions}
                                onChange={setReportType}/>
                        </Grid>
                        <Grid size={{sm: 12, md: 12, lg: 7, xl: 7}} sx={{
                            order: {
                                md: 2,
                                lg: 'unset'
                            }
                        }}>
                            {!!reportType && <Grid container spacing={2} sx={{alignItems: 'center'}}>
                                {hasPeriods && <>
                                    <Grid size={{xs: 12, sm: 6, md: 2, lg: 2, xl: 2}}>
                                        <SelectFormField name={'period'} label={t('reports.filter.period')} options={periodOptions} placeholder={t('reports.filter.selectPeriod')} shrink/>
                                    </Grid>
                                    <Grid size={{xs: 12, sm: 6, md: 2, lg: 2, xl: 2}}>
                                        <SelectFormField name={'yearFrom'} label={t('reports.filter.from')} options={yearOptions} placeholder={t('reports.filter.dateFrom')} shrink/>
                                    </Grid>
                                    <Grid size={{xs: 12, sm: 6, md: 2, lg: 2, xl: 2}}>
                                        <SelectFormField name={'yearTo'} label={t('reports.filter.to')} options={yearOptions} placeholder={t('reports.filter.dateTo')} shrink/>
                                    </Grid>
                                </>}
                                {hasContent && <Grid size={{xs: 12, sm: 6, md: 4, lg: 4, xl: 4}}>
                                    <ContentSelectFormField name={'contentId'} label={t('reports.filter.content')} placeholder={t('reports.filter.searchContent')} shrink/>
                                </Grid>}
                                {hasStudent && <Grid size={{xs: 12, sm: 6, md: 4, lg: 4, xl: 4}}>
                                    <UserSelectFormField name={'userInCompanySpaceId'} label={t('reports.filter.user')} placeholder={t('reports.filter.searchStudent')} useCsId shrink/>
                                </Grid>}
                                <Grid size={{xs: 12, sm: 6, md: 2, lg: 2, xl: 2}}>
                                    <SelectFormField name={'state'} label={t('reports.filter.state')} placeholder={t('reports.filter.selectState')} options={stateOptions} shrink/>
                                </Grid>
                            </Grid>}
                        </Grid>
                        <Grid size={{sm: 12, md: 6, lg: 3, xl: 3}} sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '8px',
                            color: 'var(--color-black)',
                            '& button': {
                                height: 'fit-content',
                                alignSelf: 'center'
                            },
                            order: {
                                md: 1,
                                lg: 'unset'
                            }
                        }}>
                            {!!data && <Tooltip title={t('reports.filter.exportReport')}>
                                <IconButton color={'inherit'} disabled={isLoading} onClick={(e) => handleExport(e, props)}><FileDownloadOutlined/></IconButton>
                            </Tooltip>}
                            {isFilterUsed && <Button variant={'contained'} color={'dark' as any} onClick={(e) => handleFilterReset(e, props)}>{t('common.clearFilters')}
                            </Button>}
                            {reportType && <Button variant={'contained'} color={'primary'} type={'submit'} disabled={isLoading}>
                                {t('reports.filter.generate')}
                            </Button>}
                        </Grid>
                    </Grid>
                </form>}
            </Formik>
        </Paper>

        {data === undefined && <ReportsMenu setReportType={setReportType}/>}

        {!!data && !hasResults && !isLoading && <Box sx={{p: 6, textAlign: 'center', color: 'text.secondary', fontSize: '16px'}}>
            {t('reports.noData')}
        </Box>}

        {!!data?.studyTimesContents && hasResults && <ReportStudyTimes data={data.studyTimesContents} type={'studyTimesContents'}/>}

        {!!data?.studyTimesStudents && hasResults && <ReportStudyTimes data={data.studyTimesStudents} type={'studyTimesStudents'}/>}

        {!!data?.testAnswers && hasResults && <ReportTestAnswersTable data={data.testAnswers}/>}

        {!!data?.testAttempts && hasResults && <ReportTestAttemptsTable data={data.testAttempts}/>}

        {isLoadMore && <Box sx={{padding: '8px', textAlign: 'center'}}>
            <Button color={'primary'} variant={'contained'} disabled={isLoading}
                onClick={() => setFilter(f => f ? {...f, page: f.page + 1, action: 'fetch'} : f)}>
                {t('common.loadMore')}
            </Button>
        </Box>}
    </>;
};
